import { Turnstile, TurnstileInstance } from '@marsidev/react-turnstile';
import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

import Link from 'components/basic/Link/Link';
import config from 'config';
import { waitAsync } from 'lib/promiseTools';

import './sharedCaptchaSettings';

const ProtectedText = styled.div`
  color: var(--gray4c);
  padding: 15px 0;
  text-align: center;
  line-height: 1.5;
  font-size: var(--small);
  /* Improve text wrap */
  max-width: 290px;
  margin: 0 auto;
`;

// https://developers.cloudflare.com/turnstile/reference/widget-types/
// https://developers.cloudflare.com/turnstile/reference/testing/

type Props = {
  reference: React.RefObject<TurnstileInstance>;
};

/** Execute async (on button click) or `false` to execute immediately */
const executeAsync = true;

/** Cloudflare Turnstile */
const InvisibleCaptchaForm = ({ reference }: Props) => {
  return (
    <>
      <Turnstile
        ref={reference}
        options={{
          execution: executeAsync ? 'execute' : 'render',
          size: 'invisible',
        }}
        siteKey={config.turnstileCaptchaSiteKey}
        // siteKey="1x00000000000000000000BB" // Always pass
        // siteKey="2x00000000000000000000BB" // Always block
        // https://developers.cloudflare.com/turnstile/reference/testing/
      />
      <ProtectedText>
        Protected by Turnstile and Cloudflare{' '}
        <Link
          href="https://www.cloudflare.com/privacypolicy/"
          color="grayText"
          underline
        >
          Privacy Policy
        </Link>{' '}
        and{' '}
        <Link
          href="https://www.cloudflare.com/website-terms/"
          color="grayText"
          underline
        >
          Terms of Service
        </Link>{' '}
        apply.
      </ProtectedText>
    </>
  );
};

export function useInvisibleReCaptcha() {
  const ref = useRef<TurnstileInstance>(null);
  const captchaForm = <InvisibleCaptchaForm reference={ref} />;
  useEffect(() => {
    const storedRef = ref.current;
    return () => {
      if (storedRef) {
        storedRef.remove();
      }
    };
  }, []);
  return {
    renderCaptchaForm: captchaForm,
    resetRecaptcha: () => {
      if (ref.current?.reset) {
        ref.current.reset();
      }
    },
    executeCaptchaAsync: async (): Promise<string> => {
      if (!ref.current) {
        throw new Error('ReCatchpa protection is not properly initialized');
      }
      // For async run we have to render & execute
      if (executeAsync) {
        ref.current.render();
        await waitAsync(200);
        ref.current.execute();
        await waitAsync(1000);
      }
      const checkExpiredAndRefresh = async () => {
        if (ref.current?.isExpired()) {
          console.log('[InvisibleCaptchaForm] Expired, resetting');
          ref.current.reset();
          if (executeAsync) {
            ref.current.execute();
          }
          await waitAsync(1000);
        }
      };

      checkExpiredAndRefresh();

      let token = ref.current.getResponse();
      let retries = 0;
      while (!token) {
        retries += 1;
        if (retries > 25) {
          throw new Error('Anti-bot response timeout');
        }
        console.debug('[InvisibleCaptchaForm] token', token);
        checkExpiredAndRefresh();
        token = ref.current.getResponse();
        await waitAsync(1000);
      }
      console.debug('[InvisibleCaptchaForm] token', token);
      if (!token) {
        throw new Error('Missing anti-bot response');
      }

      return token;
    },
  };
}
